import image from 'images/devices/keon-promotion.png';
import * as DeviceStates from './DeviceStates';

import Stroker from './Stroker';
import { CONNECTION_WAY } from 'data/constants';
import { toFullUUID } from 'utils/bluetooth-uuid-helper';
import { tranformDataToArray } from 'utils/helpers';
import { wait } from 'utils/wait';
import { dataViewToAsciiString } from './DeviceUtils';

const PRIMARY_SERVICE_UUID: BluetoothCharacteristicUUID = 0x1900;
const BATTERY_STATUS_CHAR_UUID: BluetoothCharacteristicUUID = 0x2a19;
const MOTOR_COMMAND_CHAR_UUID: BluetoothCharacteristicUUID = 0x1800;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TOUCH_CHAR_UUID: BluetoothCharacteristicUUID = 0x1600;

const PROVISIONING_CHAR_UUID: BluetoothCharacteristicUUID = 0x2001;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FIRMWARE_VERSION_CHAR_UUID: BluetoothCharacteristicUUID = 0x1901;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MANUFACTURE_NAME_CHAR_UUID: BluetoothCharacteristicUUID = 0x1902;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SERIAL_NUMBER_CHAR_UUID: BluetoothCharacteristicUUID = 0x1903;

// Flash firmware characteristic UUIDs
/**
 * UUID for OTA (Over-the-Air) firmware data characteristic.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OTA_DATA_CHAR_UUID: BluetoothCharacteristicUUID = 0x1701;
/**
 * UUID for OTA control characteristic.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OTA_CONTROL_CHAR_UUID: BluetoothCharacteristicUUID = 0x1702;

const names = ['KEON WIFI'];

const KEON_MIN_POS: number = 0;
const KEON_MAX_POS: number = 90;
const KEON_FULL_CYCLE_TIME_MSEC: number = 200;

const PREFIX_SSID_NAME: string = '0';
const PREFIX_PASSWORD: string = '1';
const PREFIX_TOKEN: string = '2';

const PROV_CHANGE_MODE_WIFI: number = 0x1;
const PROV_START: number = 0x2;
const PROV_CRED_CONFIRM: number = 0x3;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PROV_REVERT: number = 0x4;
const PROV_CLEAN_LIST: number = 0x5;
/**
 * Default Maximum Transmission Unit (MTU) size in bytes.
 */
const DEFAULT_MTU_SIZE: number = 512;

type ConnectionMode = (typeof CONNECTION_WAY)[keyof typeof CONNECTION_WAY];

interface DeviceStatusData {
  sid: string;
  bleAddress: string;
  serialNumber: string;
  batteryStatus: number;
  encoderPos: number;
  motorSpeed: number;
  wifiStrength: number;
  intensity: number;
  firmwareVersion: string;
  strokerLevel: number;
  statusPeriod: number;
  errorCode: number;
}
const initialDeviceStatusData: DeviceStatusData = {
  sid: '',
  bleAddress: '',
  serialNumber: '',
  batteryStatus: 0,
  encoderPos: 0,
  motorSpeed: 0,
  wifiStrength: 0,
  intensity: 8,
  firmwareVersion: '',
  strokerLevel: 4,
  statusPeriod: 100,
  errorCode: 0,
};

export default class KeonWiFi extends Stroker {
  private deviceNotInitMsg = 'The device should be connected first.';
  private credsNotFound = 'The credentials are invalid or not found';
  private wentWrong = 'Something went wrong...';

  supportProvisioning: boolean;
  connectionMode: ConnectionMode;
  fnSendMessageToDevices: (eventName: string, args: any) => void;

  private statusPackageVersionMin = 'V1.4';
  deviceStatusData: DeviceStatusData = initialDeviceStatusData;

  constructor(device: BluetoothDevice) {
    super(
      device,
      toFullUUID(PRIMARY_SERVICE_UUID),
      toFullUUID(MOTOR_COMMAND_CHAR_UUID),
      image,
      toFullUUID(BATTERY_STATUS_CHAR_UUID),
      KEON_MAX_POS,
      KEON_MIN_POS,
      KEON_FULL_CYCLE_TIME_MSEC,
    );
    this.supportProvisioning = true;
    this.connectionMode = CONNECTION_WAY.BT;
  }

  static get deviceNames() {
    return names;
  }

  static get services() {
    return [toFullUUID(PRIMARY_SERVICE_UUID)];
  }

  get companyName() {
    return 'Kiiroo';
  }

  getConnectionMode = (): ConnectionMode => {
    return this.connectionMode;
  };

  sendCommandToDevices = (commandType: string, args: any) => {
    this.fnSendMessageToDevices('send_command_to_devices', {
      command_type: commandType,
      arguments: args,
    });
  };

  sendSetupToDevice = (setupType: string, args: any) => {
    this.fnSendMessageToDevices('send_settings_to_device', {
      setup_type: setupType,
      arguments: args,
    });
  };

  sendReprovisionCommandToDevice = (reprovisionType: string, args: any = null) => {
    this.fnSendMessageToDevices('send_reprovision_command_to_device', {
      reprovision_type: reprovisionType,
      arguments: args,
    });
  };

  switchToBtMode = () => {
    this.sendReprovisionCommandToDevice('bt_mode', {});
    this.setConnectionState(DeviceStates.DISCONNECTED);
    this.connectionMode = CONNECTION_WAY.BT;
  };

  resetDeviceStatus = () => {
    this.sendReprovisionCommandToDevice('reset_credentials', {});
    this.deviceStatusData = initialDeviceStatusData;
    this.setConnectionState(DeviceStates.DISCONNECTED);
    this.connectionMode = CONNECTION_WAY.BT;
  };

  async connect(isConnectionByWifi) {
    if (isConnectionByWifi) {
      this.setConnectionState(DeviceStates.CONNECTED);
      this.connectionMode = CONNECTION_WAY.WIFI;
    } else {
      this.connectionMode = CONNECTION_WAY.BT;
      await super.connect();
      // Move Keon to the movement mode
      await this.motorChar.writeValue(tranformDataToArray([0x00]));
      await this.motorChar.startNotifications();
      this.motorChar.addEventListener('characteristicvaluechanged', (event) => {
        const valueArray = tranformDataToArray(event.currentTarget.value.buffer);
        const pos = 100 - valueArray[1]; // because it's reversed, 0 is down (full penitration)
        this.deviceMovementCallback({ position: pos, speed: 0 });
      });

      let provChar: BluetoothRemoteGATTCharacteristic = await this.sensorService.getCharacteristic(
        SERIAL_NUMBER_CHAR_UUID,
      );
      let statusResponse = await provChar.readValue();
      this.deviceStatusData.serialNumber = dataViewToAsciiString(statusResponse);
    }
  }

  async disconnect(removeDevice: boolean = false) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await super.disconnect();
    } else {
      this.switchToBtMode();
    }
  }
  async moveTo(position: number, speed: number, isOntimer?: boolean): Promise<void> {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      super.moveTo(position, speed);
    } else {
      if (isOntimer) {
        const pos = Math.max(
          this.minPosition,
          Math.min(this.maxPosition, Math.round(position * 0.7)),
        );
        const spd = Math.max(0, Math.min(99, Math.round(speed * 0.5)));

        this.sendCommandToDevices('MOVEMENT', {
          speed: spd,
          position: pos,
        });
      }
    }
  }

  async writeValue(position: number, speed: number) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await this.motorChar.writeValue(tranformDataToArray([0x03, 0x01, speed, position]));
    }
  }

  async writePauseValue() {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await this.motorChar.writeValue(tranformDataToArray([0x00]));
    } else {
      this.sendCommandToDevices('PAUSE', {});
    }
  }

  async writeMovementBetween(minPosition: number, maxPosition: number, speed: number) {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      await this.motorChar.writeValue(
        tranformDataToArray([0x04, 0x00, speed, minPosition, maxPosition]),
      );
    } else {
      this.sendCommandToDevices('MOVEMENT_BETWEEN', {
        speed,
        min_position: minPosition,
        max_position: maxPosition,
      });
    }
  }

  async getBattery() {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      try {
        const readData = await this.statusChar.readValue();
        if (readData) {
          return readData.getUint8(0);
        }
      } catch (e) {
        console.error('Keon WiFi.getBattery:', e);
      }
      return -1;
    } else {
      return this.deviceStatusData.batteryStatus;
    }
  }

  isConnected(): boolean {
    return !!this.device?.gatt?.connected;
  }

  async provisioning(ssid: string, psswrd: string, token: string): Promise<void> {
    if (!this.sensorService || !this.isConnected) {
      return Promise.reject(this.deviceNotInitMsg);
    }
    if (!ssid || !psswrd || !token) {
      return Promise.reject(this.credsNotFound);
    }

    const handleResponse = async (stage: string, expectedStatusCode: number) => {
      let statusResponse = (await provChar.readValue()).getUint8(0);
      if (statusResponse !== expectedStatusCode) {
        console.error(`Unexpected status code: ${statusResponse} at provosioning step ${stage}`);
        return Promise.reject(this.wentWrong);
      }
    };

    let provChar: BluetoothRemoteGATTCharacteristic = await this.sensorService.getCharacteristic(
      PROVISIONING_CHAR_UUID,
    );

    let dt = tranformDataToArray([PROV_CLEAN_LIST]);
    await provChar.writeValue(dt);
    await handleResponse('PROV_CLEAN_LIST', 17);

    dt = tranformDataToArray([PROV_CHANGE_MODE_WIFI]);
    await provChar.writeValue(dt);
    await handleResponse('PROV_CHANGE_MODE_WIFI', 17);

    dt = tranformDataToArray([PROV_START]);
    await provChar.writeValue(dt);
    await handleResponse('PROV_START', 17);

    dt = tranformDataToArray(PREFIX_SSID_NAME + ssid);
    await provChar.writeValue(dt);

    dt = tranformDataToArray(PREFIX_PASSWORD + psswrd);
    await provChar.writeValue(dt);
    await handleResponse('PREFIX_PASSWORD', 17);

    // Token (you should send max 500 characters for each operation)
    const packetSize = DEFAULT_MTU_SIZE - 12;
    for (let i = 0; i < token.length; i += packetSize) {
      const chunk = token.slice(i, i + packetSize);
      dt = tranformDataToArray(PREFIX_TOKEN + chunk);
      await provChar.writeValue(dt);
    }
    await handleResponse('PREFIX_TOKEN:' + token.length, 17);

    dt = tranformDataToArray([PROV_CRED_CONFIRM]);
    await provChar.writeValue(dt);
    await handleResponse('PROV_CRED_CONFIRM', 17);
  }

  async setMaxIntensity(percent: number) {
    super.setMaxIntensity(percent);
    if (this.connectionMode === CONNECTION_WAY.WIFI) {
      // intensity is adjusted as in manual mode
      const speedIntensity = Math.min(8, Math.floor((percent / 100) * 8) + 1);
      const rangeIntensity = Math.min(4, Math.floor((percent / 100) * 4) + 1);
      this.sendSetupToDevice('speed_intensity_adjustment', { intensity: speedIntensity });
      this.sendSetupToDevice('range_intensity_adjustment', { intensity: rangeIntensity });
      this.sendSetupToDevice('status_update_interval', { interval: 100 });
    }
  }

  async testDevice() {
    if (this.connectionMode === CONNECTION_WAY.BT) {
      return await super.testDevice();
    } else {
      this.sendCommandToDevices('MOVEMENT_BETWEEN', {
        speed: 100,
        min_position: 0,
        max_position: 100,
      });
      await wait(5000);

      this.sendCommandToDevices('PAUSE', {});
    }
  }

  refreshStatus(sid: string, deviceInfo: any): void {
    this.deviceStatusData.sid = sid;
    const statusPackage = deviceInfo?.status_package;
    if (statusPackage !== this.statusPackageVersionMin) {
      console.error(
        `Expected status package is ${this.statusPackageVersionMin} but we got ${statusPackage}.`,
      );
    }
    this.deviceStatusData.bleAddress = deviceInfo?.ble_address;
    this.deviceStatusData.serialNumber = deviceInfo?.serial_number;
    this.deviceStatusData.batteryStatus = deviceInfo?.battery_status;
    this.deviceStatusData.encoderPos = deviceInfo?.encoder_pos;
    this.deviceStatusData.motorSpeed = deviceInfo?.motor_speed;
    this.deviceStatusData.wifiStrength = deviceInfo?.wifi_strength;
    this.deviceStatusData.intensity = deviceInfo?.intensity;
    this.deviceStatusData.strokerLevel = deviceInfo?.stroker_level;
    this.deviceStatusData.statusPeriod = deviceInfo?.status_period;
    this.deviceStatusData.errorCode = deviceInfo?.error_code;
    this.deviceStatusData.firmwareVersion = deviceInfo?.firmware_version;
  }

  public getStatus(): DeviceStatusData {
    return this.deviceStatusData;
  }

  setServerChanels(sendMessageToDevices: (eventName: string, args: any) => void): void {
    this.fnSendMessageToDevices = sendMessageToDevices;
  }

  /**
   * Starts the ambient movement for the device
   * (The sequence of repetitive movements sent to the device when no action is happening)
   */
  async onTimer() {
    // If ambient movement is below this, device doesn't move
    const AMBIENT_MOVEMENT_MIN_THRESHOLD = 3;

    const speed = +this.ambientMovementMixin.ambientIntensity;
    if (speed < AMBIENT_MOVEMENT_MIN_THRESHOLD) {
      // If no ambient movement, do nothing
      return;
    }

    if (performance.now() < this.ambientMovementMixin.ambientNextMovementTimestamp) {
      // If didn't pass enough time since we moved for the last time, do nothing
      return;
    }
    const newPos = 100 - this.ambientMovementMixin.currentPosition;
    await this.moveTo(newPos, speed, true);
    this.ambientMovementMixin.currentPosition = newPos;
    const ambientMovementIntervalBase = 20000 / speed;
    const isWifi = this.connectionMode === CONNECTION_WAY.WIFI;
    // Slow down a little bit for WiFi mode to avoid sending commands too frequently
    const ambientMovementInterval = ambientMovementIntervalBase * (isWifi ? 1.5 : 1);
    this.ambientMovementMixin.ambientNextMovementTimestamp =
      performance.now() + ambientMovementInterval;
  }
}
